import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

import { buildClassName, scrollToEl } from '../internals/react-utils';
import { registerKey, unregisterKey } from '../internals/global-shortcuts';

import { entitySelectedFromSearchPalette } from '../events/entitySelectedFromSearchPalette.event';

import { CommandPaletteSearchPanel } from '../components/command-palette/CommandPaletteSearchPanel.view';
import { ResultsList } from '../components/command-palette/CommandPaletteResults.view';
import {
  getResults,
  makeCreateNewNeedle,
  makeGoToNiNeedle,
  makeSearchNeedle,
} from '../components/command-palette/CommandPaletteRenderers.view';

import { TestID } from '../testID';

const prefixRegExp = /^([\w-]+):(.*)$/;
const MAX_MATCHES = 25;

const searchTypes = {
  PIPE: 'pipe',
  DATASET: 'dataset',
  SYSTEM: 'system',
  GLOBAL: 'global',
  ALL: '',
};

const promoteOriginUser = (a, b) => {
  if (a.item.origin && b.item.origin) {
    if (a.item.origin === 'user' && b.item.origin !== 'user') {
      return -1;
    } else if (a.item.origin !== 'user' && b.item.origin === 'user') {
      return 1;
    } else {
      return 0;
    }
  } else if (a.item.origin && !b.item.origin) {
    return -1;
  } else if (!a.item.origin && b.item.origin) {
    return 1;
  } else {
    return 0;
  }
};

const compareNeedlesForDisplay = (a, b) => {
  const originDiff = promoteOriginUser(a, b);
  if (originDiff !== 0) {
    return originDiff;
  }

  const aId = a?.item?.id || '';
  const bId = b?.item?.id || '';
  const lengthDiff = aId.length - bId.length;

  if (lengthDiff !== 0) {
    return lengthDiff;
  }

  return aId.localeCompare(bId);
};

/**
 * An overlay command palette, à la Spotlight
 */
class CommandPalette extends React.Component {
  constructor(props) {
    super(props);

    this.rootEl = React.createRef();
    this.inputRef = React.createRef();
    this.selectionBeforeToggle = null;

    this.handleClick = (ev) => {
      if (!this.rootEl.current.contains(ev.target)) {
        this.props.onRequestClose();
      }
    };

    this.recalculateResults = (inputValue, searchTypeOverride = this.state.searchType) => {
      let input = inputValue;
      let results = [];
      let prefix = '';
      let searchType = searchTypeOverride;
      let searchFor = input;

      if (input !== '') {
        // there is some input, is there a prefix? (pipe:,system:, dataset:)
        const prefixRegExpResult = input.match(prefixRegExp);
        if (prefixRegExpResult) {
          prefix = prefixRegExpResult[1];
          searchFor = prefixRegExpResult[2];
        }
        if (prefix && Object.values(searchTypes).includes(prefix)) {
          // slice out the prefix from the input because we add
          // the searchtype message in front
          searchType = prefix;
          input = input.slice(prefix.length + 1);
        }
        // if there is not prefix but we are within a search type, ie
        // there was a prefix before
        if (this.state.searchType && prefix === '') prefix = searchType;
        results = getResults(
          prefix,
          searchFor,
          this.props.haystackGenerator,
          this.props.maxMatches
        );
      } else {
        if (searchType) {
          results = getResults(searchType, '', this.props.haystackGenerator, this.props.maxMatches);
        }
      }

      results = results.slice().sort(compareNeedlesForDisplay);

      return { results, input, searchType };
    };

    this.handleOnChange = (ev) => {
      this.selectionBeforeToggle = null;

      let input = ev.target.value;

      // was the onChange event a Backspace key?
      const isBackspace = ev.nativeEvent.inputType === 'deleteContentBackward';
      // was the onChange vent a Delete key?
      const isDelete = ev.nativeEvent.inputType === 'deleteContentForward';

      const recalculated = this.recalculateResults(input);
      let results = recalculated.results;
      let searchType = recalculated.searchType;
      input = recalculated.input;
      const userInput = input;

      const originalInputLen = input.length;

      // autocomplete the input with the first result
      if (results.length > 0 && !isBackspace && !isDelete) {
        const firstResultId = results[0].item['id'];
        if (firstResultId.startsWith(input)) {
          input = input + firstResultId.slice(input.length);
        }
      }

      ev.persist(); // needed to reference the event in the setState callback

      // update the "create new" results with current input
      const newPipeNeedle = makeCreateNewNeedle(input, this.props.router, this.props.subId, 'pipe');

      const newSystemNeedle = makeCreateNewNeedle(
        input,
        this.props.router,
        this.props.subId,
        'system'
      );

      const goToNiNeedle = makeGoToNiNeedle(input, this.props.router, this.props.subId);

      const searchNeedle = makeSearchNeedle(input, this.props.router, this.props.subId);

      const emptyResultNeedles = [newPipeNeedle, newSystemNeedle];

      if (searchType === 'system') {
        emptyResultNeedles.reverse();
      }

      if (this.props.hasSearch) {
        if (input.indexOf(':') !== -1) {
          emptyResultNeedles.push(goToNiNeedle);
        }

        emptyResultNeedles.unshift(searchNeedle);

        if (results.length > 0) {
          results.push(searchNeedle);
        }
      }

      this.setState(
        {
          results,
          input,
          userInput,
          selectedNeedleIdx: 0,
          searchType,
          emptyResultNeedles,
        },
        () => {
          // select the autocompleted part of input
          if (results.length > 0 && !isBackspace && !isDelete) {
            ev.target.setSelectionRange(originalInputLen, input.length);
          }
        }
      );
    };

    this.handleIncludeInternalObjectsChange = (ev) => {
      const includeInternalObjects = ev.target.checked;
      const inputEl = this.inputRef.current;

      if (inputEl) {
        this.selectionBeforeToggle = {
          value: inputEl.value,
          start: inputEl.selectionStart,
          end: inputEl.selectionEnd,
        };
      }

      if (this.props.onIncludeInternalObjectsChange) {
        this.props.onIncludeInternalObjectsChange(includeInternalObjects);
      }

      this.setState(
        {
          includeInternalObjects,
          selectedNeedleIdx: 0,
        },
        () => {
          if (this.inputRef.current) {
            this.inputRef.current.focus();
          }

          this.restoreAutocompleteSelection();
        }
      );
    };

    this.restoreAutocompleteSelection = () => {
      const inputEl = this.inputRef.current;
      const { input, userInput } = this.state;

      if (!inputEl || !input) {
        return;
      }

      if (
        this.selectionBeforeToggle &&
        this.selectionBeforeToggle.value === inputEl.value &&
        this.selectionBeforeToggle.start !== null &&
        this.selectionBeforeToggle.end !== null
      ) {
        const safeStart = Math.min(this.selectionBeforeToggle.start, inputEl.value.length);
        const safeEnd = Math.min(this.selectionBeforeToggle.end, inputEl.value.length);

        inputEl.setSelectionRange(safeStart, safeEnd);
        return;
      }

      const typedLength = userInput ? userInput.length : 0;
      const safeCaretPosition = Math.min(typedLength, input.length);

      if (!userInput || input.length <= userInput.length) {
        inputEl.setSelectionRange(safeCaretPosition, safeCaretPosition);
        return;
      }

      if (!input.startsWith(userInput) || input.length <= userInput.length) {
        inputEl.setSelectionRange(safeCaretPosition, safeCaretPosition);
        return;
      }

      inputEl.setSelectionRange(userInput.length, input.length);
    };

    this.handleArrowDown = (ev) => {
      ev.preventDefault();

      if (this.state.selectedNeedleIdx < MAX_MATCHES) {
        let results = this.state.results;
        if (this.isShowingRecent()) results = this.props.recentResults;
        if (this.isShowingNothingFound()) {
          results = this.state.emptyResultNeedles;
        }

        if (this.state.selectedNeedleIdx < results.length - 1) {
          const updatedNeedleIdx = this.state.selectedNeedleIdx + 1;
          let newInput = this.state.input;

          if (this.isShowingResults()) {
            newInput = results[updatedNeedleIdx].item['id'] || results[updatedNeedleIdx].item;
          }

          this.setState({
            selectedNeedleIdx: updatedNeedleIdx,
            input: newInput,
          });
        }
      }
    };

    this.handleArrowUp = (ev) => {
      ev.preventDefault();

      if (this.state.selectedNeedleIdx > 0) {
        let results = this.state.results;
        if (this.isShowingRecent()) results = this.props.recentResults;
        if (this.isShowingNothingFound()) {
          results = this.state.emptyResultNeedles;
        }

        const updatedNeedleIdx = this.state.selectedNeedleIdx - 1;
        let newInput = this.state.input;

        if (this.isShowingResults()) {
          newInput = results[updatedNeedleIdx].item['id'] || results[updatedNeedleIdx].item;
        }

        this.setState({
          selectedNeedleIdx: updatedNeedleIdx,
          input: newInput,
        });
      }
    };

    this.handleSubmit = (results, ev) => {
      ev.preventDefault();

      entitySelectedFromSearchPalette.emit();

      const needle = results[this.state.selectedNeedleIdx];

      if (needle !== undefined) {
        needle.onActivate(needle.item.id, needle.item.name);
        this.props.onRequestClose();
      }
    };

    this.handleNeedleClick = (needle) => (ev) => {
      ev.preventDefault();

      entitySelectedFromSearchPalette.emit();

      this.props.onRequestClose();
      needle.onActivate(needle.item.id, needle.item.name);
    };

    this.handleRemovePrefixSearch = (ev) => {
      if (this.state.input === '' && this.state.searchType && ev.keyCode === 8) {
        // input is empty but we are in a prefix search
        // so remove that prefix search
        ev.preventDefault();
        const searchType = '';
        const input = this.state.searchType;
        this.setState({ searchType, input });
      }
    };

    this.routerListenerInited = false;
    this.isShowingResults = () => this.state.results.length > 0 && this.state.input.length > 0;

    this.isShowingRecent = () =>
      this.state.results.length < 1 &&
      this.state.input.length < 1 &&
      !this.state.searchType &&
      this.props.recentResults.length > 0;

    this.isShowingNothingFound = () => this.state.results.length < 1 && this.state.input.length > 0;

    this.state = {
      input: '',
      userInput: '',
      searchType: searchTypes.ALL,
      results: this.props.recentResults,
      includeInternalObjects: this.props.initialIncludeInternalObjects ?? false,
      selectedNeedleIdx: 0,
      emptyResultNeedles: [],
    };
  }

  componentDidMount() {
    if (this.props.onIncludeInternalObjectsChange) {
      this.props.onIncludeInternalObjectsChange(this.props.initialIncludeInternalObjects ?? false);
    }

    registerKey('ArrowDown', this.handleArrowDown, {}, true, true);
    registerKey('ArrowUp', this.handleArrowUp, {}, true, true);
    registerKey('Escape', this.props.onRequestClose, {}, true, true);
    registerKey('Tab', this.handleArrowDown, {}, true, true);
    registerKey('Tab', this.handleArrowUp, { shiftKey: true }, true, true);
    document.addEventListener('mousedown', this.handleClick);
    this.unregisterRouterListener = this.props.router.listen(() => {
      // When registering this listener, it fires immediately with the latest
      // transition. We need a flag to ignore the initial call.
      if (this.routerListenerInited) {
        this.props.onRequestClose();
      }
      this.routerListenerInited = true;
    });
  }

  componentWillUnmount() {
    unregisterKey('ArrowDown');
    unregisterKey('ArrowUp');
    unregisterKey('Escape');
    unregisterKey('Tab');
    document.removeEventListener('mousedown', this.handleClick);
    this.unregisterRouterListener();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.haystackVersion !== this.props.haystackVersion) {
      const input = this.state.userInput;
      const searchType = this.state.searchType;
      let results = [];

      if (input !== '' || searchType) {
        results = this.recalculateResults(input, searchType).results;
      }

      this.setState({ results, selectedNeedleIdx: 0 }, this.restoreAutocompleteSelection);
    }
  }

  render() {
    let results = this.state.results;

    if (this.isShowingRecent()) {
      results = this.props.recentResults;
    }

    if (this.isShowingNothingFound()) {
      results = this.state.emptyResultNeedles;
    }

    const selIdx = this.state.selectedNeedleIdx;
    const selectedResult = results.length > 0 ? results[selIdx] : undefined;

    return (
      <CommandPaletteSearchPanel
        results={results}
        searchType={this.state.searchType}
        input={this.state.input}
        includeInternalObjects={this.state.includeInternalObjects}
        selectedResult={selectedResult}
        selIdx={selIdx}
        rootEl={this.rootEl}
        inputRef={this.inputRef}
        isShowingRecent={this.isShowingRecent()}
        isShowingNothingFound={this.isShowingNothingFound()}
        handleSubmit={this.handleSubmit}
        handleOnChange={this.handleOnChange}
        handleIncludeInternalObjectsChange={this.handleIncludeInternalObjectsChange}
        handleRemovePrefixSearch={this.handleRemovePrefixSearch}
        handleNeedleClick={this.handleNeedleClick}
        darkModeActive={this.props.darkModeActive}
      />
    );
  }
}

CommandPalette.propTypes = {
  /** A hint to display when opened */
  hint: PropTypes.node,

  /** {CommandPalette~haystackGenerator} Generator for stuff to search */
  haystackGenerator: PropTypes.func.isRequired,

  /** Maximum number of matches to display */
  maxMatches: PropTypes.number,

  /** Handler for when the user requests closing the palette (e.g. by pressing
      `Esc`) — this should update the parent component's state so as to unmount
      this component */
  onRequestClose: PropTypes.func.isRequired,

  /** {Router} */
  router: PropTypes.object.isRequired,
  recentResults: PropTypes.array.isRequired,
  subId: PropTypes.string.isRequired,
  hasSearch: PropTypes.bool.isRequired,
  isItestMode: PropTypes.bool,
  onIncludeInternalObjectsChange: PropTypes.func,
  haystackVersion: PropTypes.any,
  initialIncludeInternalObjects: PropTypes.bool,
  darkModeActive: PropTypes.bool,
};

CommandPalette.defaultProps = {
  hint: undefined,
  maxMatches: MAX_MATCHES,
};

const Routed = withRouter(CommandPalette);

export { Routed as CommandPalette };

/**
 * @callback CommandPalette~haystackGenerator
 * @generator
 * @param {string} [prefix=""] A prefix to help the generator limit what to return
 * @yields {CommandPalette~needle}
 */

/**
 * @typedef {Object} CommandPalette~needle
 * @property {CommandPalette~matcherCallback} matcher A function that evaluates whether the item matches the search
 * @property {Object} item An arbitrary data obhect
 * @property {CommandPalette~activateCallback} onActivate Callback for when the user activates a result
 * @property {CommandPalette~rendererCallback} renderer Callback for rendering the item within the results list
 * @property {CommandPalette~keyMakerCallback} keyMaker Callback for generating a unique ID for React's `key`
 */

/**
 * @callback CommandPalette~matcherCallback
 * @param {object} item The data item being evaluated; @see {CommandPalette~needle} (the item param)
 * @param {RegExp} search A regular expression to use for testing strings against
 * @returns {bool} Whether the item matches the search
 */

/**
 * @callback CommandPalette~activateCallback
 * @param {object} item The data item being evaluated; @see {CommandPalette~needle} (the item param)
 */

/**
 * @callback CommandPalette~rendererCallback
 * @param {object} item The data item being evaluated; @see {CommandPalette~needle} (the item param)
 * @returns {Node} Contents to display as a result item
 */

/**
 * @callback CommandPalette~keyMakerCallback
 * @param {object} item The data item being evaluated; @see {CommandPalette~needle} (the item param)
 * @returns {string} A unique ID (the same item should always generate the same ID)
 */
